import { useCallback, useEffect, useState } from "react"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import { FilterDropdown } from "../../shared/ui/FilterDropdown"
import { CopyableId } from "../../shared/ui/CopyableId"
import {
  listPaymentsApi,
  checkPaymentStatusApi,
  PaymentItem,
  PaymentStatus,
} from "../../features/payments/api/payments.api"
import {
  CreditCardIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  CheckCircleIcon,
} from "@heroicons/react/16/solid"

const LIMIT = 20

const STATUS_CONFIG: Record<PaymentStatus, { label: string; className: string; dot: string }> = {
  pending:   { label: "Ожидает",   className: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",     dot: "bg-amber-400" },
  completed: { label: "Завершён",  className: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200", dot: "bg-emerald-400" },
  failed:    { label: "Ошибка",    className: "bg-red-50 text-red-700 ring-1 ring-red-200",           dot: "bg-red-400" },
}

const StatusBadge = ({ status }: { status: PaymentStatus }) => {
  const cfg = STATUS_CONFIG[status] ?? { label: status, className: "bg-gray-100 text-gray-500" }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  )
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })

const formatMoney = (amount: number | undefined | null, currency: string | undefined | null) =>
  typeof amount === "number" ? `${amount.toLocaleString("ru-RU")} ${currency ?? ""}`.trim() : "—"

export const PaymentsPage = () => {
  const [items, setItems] = useState<PaymentItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)

  const [filterStatus, setFilterStatus] = useState<PaymentStatus | "">("")
  const [orderId, setOrderId] = useState("")
  const [pendingOrderId, setPendingOrderId] = useState("")

  const [checkingId, setCheckingId] = useState<string | null>(null)
  const [toast, setToast] = useState<{ text: string; ok: boolean } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const offset = (page - 1) * LIMIT
    const res = await listPaymentsApi({ limit: LIMIT, offset, status: filterStatus, order_id: orderId || undefined })
    setItems(res.data.items)
    setTotal(res.data.total)
    setLoading(false)
  }, [page, filterStatus, orderId])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(1) }, [filterStatus, orderId])

  const applyOrderFilter = () => setOrderId(pendingOrderId.trim())
  const clearOrderFilter = () => { setOrderId(""); setPendingOrderId("") }

  const showToast = (text: string, ok: boolean) => {
    setToast({ text, ok })
    setTimeout(() => setToast((t) => (t?.text === text ? null : t)), 4000)
  }

  const handleCheckStatus = async (payment: PaymentItem) => {
    setCheckingId(payment.id)
    try {
      const res = await checkPaymentStatusApi(payment.id)
      setItems((prev) => prev.map((p) => (p.id === payment.id ? res.data : p)))
      showToast(
        res.data.status !== payment.status ? `Статус обновлён: ${STATUS_CONFIG[res.data.status]?.label ?? res.data.status}` : "Статус подтверждён, изменений нет",
        true
      )
    } catch {
      showToast("Не удалось проверить статус", false)
    } finally {
      setCheckingId(null)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <DashboardLayout title="Платежи">
      <div className="bg-white rounded-xl border overflow-hidden">

        {/* ── Filters ── */}
        <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-b bg-gray-50/60">
          <FunnelIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />

          <FilterDropdown
            label="Статус"
            value={filterStatus}
            onChange={setFilterStatus}
            options={(Object.keys(STATUS_CONFIG) as PaymentStatus[]).map((s) => ({ value: s, label: STATUS_CONFIG[s].label, dot: STATUS_CONFIG[s].dot }))}
          />

          <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-1.5 bg-white">
            <MagnifyingGlassIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <input
              type="text"
              placeholder="ID заказа…"
              value={pendingOrderId}
              onChange={(e) => setPendingOrderId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyOrderFilter()}
              className="w-36 text-xs outline-none font-mono"
            />
          </div>
          <button onClick={applyOrderFilter} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors">
            Найти
          </button>

          {(filterStatus || orderId) && (
            <button
              type="button"
              onClick={() => { setFilterStatus(""); clearOrderFilter() }}
              className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
            >
              Сбросить фильтры
            </button>
          )}

          <span className="ml-auto text-xs text-gray-400">{!loading && `${total} платежей`}</span>
        </div>

        {/* ── Table ── */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50/40">
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">ID заказа</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Сумма</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Провайдер</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Статус</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Дата</th>
                <th className="px-5 py-3 w-40" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {[1, 2, 3, 4, 5, 6].map((j) => (
                      <td key={j} className="px-5 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse w-24" /></td>
                    ))}
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-16 text-center">
                    <CreditCardIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Платежей не найдено</p>
                  </td>
                </tr>
              ) : (
                items.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50/60 transition-colors group">
                    <td className="px-5 py-3.5"><CopyableId value={p.order_id} /></td>
                    <td className="px-5 py-3.5 font-medium text-gray-800">{formatMoney(p.amount, p.currency)}</td>
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full capitalize">{p.provider}</span>
                    </td>
                    <td className="px-5 py-3.5"><StatusBadge status={p.status} /></td>
                    <td className="px-5 py-3.5 text-xs text-gray-400 whitespace-nowrap">{formatDate(p.created_at)}</td>
                    <td className="px-5 py-3.5">
                      {p.status === "pending" && (
                        <button
                          disabled={checkingId === p.id}
                          onClick={() => handleCheckStatus(p)}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-40 transition-colors px-2.5 py-1.5 rounded-lg hover:bg-blue-50"
                        >
                          <ArrowPathIcon className={`w-3.5 h-3.5 ${checkingId === p.id ? "animate-spin" : ""}`} />
                          Проверить статус
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t bg-gray-50/40">
            <span className="text-xs text-gray-400">
              Страница <span className="font-medium text-gray-600">{page}</span> из <span className="font-medium text-gray-600">{totalPages}</span>
            </span>
            <div className="flex gap-1.5">
              <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">← Назад</button>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Вперёд →</button>
            </div>
          </div>
        )}
      </div>

      {toast && (
        <div className={`fixed top-5 right-5 z-[60] flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium shadow-lg ${toast.ok ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>
          <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
          {toast.text}
        </div>
      )}
    </DashboardLayout>
  )
}